import React, { useState, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useProject } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './Renders.css';

interface RenderItem {
  version: number;
  status: string;
  created_at: string;
  file_name: string;
  video_settings: {
    background_color?: string;
    fps_num?: number;
    video_width?: number;
    transparent?: boolean;
    video_time?: number;
    aspect_ratio?: string;
    video_format?: string;
  };
}

// Mock API function for list_renders
// const mockListRenders = async (projectId: string): Promise<RenderItem[]> => {
//   // Simulate API delay
//   await new Promise(resolve => setTimeout(resolve, 300));
  
//   return [
//     {
//       version: 29,
//       status: 100,
//       start_time: '2025-10-11 10:30:00',
//       end_time: '2025-10-11 10:35:00',
//       background_color: '#000000',
//       fps_num: 30,
//       video_width: 1920,
//       transparent: false,
//       video_time: 120,
//       aspect_ratio: '16:9'
//     },
//     {
//       version: 28,
//       status: 65,
//       start_time: '2025-10-11 09:15:00',
//       end_time: null,
//       background_color: '#FFFFFF',
//       fps_num: 24,
//       video_width: 3840,
//       transparent: false,
//       video_time: 180,
//       aspect_ratio: '16:9'
//     },
//     {
//       version: 27,
//       status: -1,
//       start_time: '2025-10-11 08:00:00',
//       end_time: '2025-10-11 08:05:00',
//       background_color: '#000000',
//       fps_num: 30,
//       video_width: 1920,
//       transparent: true,
//       video_time: 150,
//       aspect_ratio: '21:9'
//     },
//     {
//       version: 26,
//       status: 100,
//       start_time: '2025-10-10 16:45:00',
//       end_time: '2025-10-10 16:50:00',
//       background_color: '#1a1a1a',
//       fps_num: 60,
//       video_width: 1920,
//       transparent: false,
//       video_time: 90,
//       aspect_ratio: '16:9'
//     },
//     {
//       version: 25,
//       status: 33,
//       start_time: '2025-10-10 14:20:00',
//       end_time: null,
//       background_color: '#000000',
//       fps_num: 30,
//       video_width: 2560,
//       transparent: false,
//       video_time: 200,
//       aspect_ratio: '16:9'
//     }
//   ];
// };

const realListRenders = async (projectId: string): Promise<RenderItem[]> => {
  const response = await fetch(`${getApiUrl()}/list-renders?projectId=${projectId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch renders');
  }
  const data = await response.json();
  return data.renders || [];
};

// Active API function - switch between mock and real
// const listRenders = mockListRenders;
const listRenders = realListRenders;

// ============================================================================

// Mock API function for get_render
// const mockGetRender = async (projectId: string, version: number, fileName: string): Promise<void> => {
//   // Simulate API delay
//   await new Promise(resolve => setTimeout(resolve, 500));
  
//   // Create a mock video file download
//   const blob = new Blob(['Mock video content'], { type: 'video/mp4' });
//   const url = window.URL.createObjectURL(blob);
//   const link = document.createElement('a');
//   link.href = url;
//   link.download = `render_v${version}_${new Date().toISOString().split('T')[0]}.mp4`;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
//   window.URL.revokeObjectURL(url);
// };

const realGetRender = async (
  projectId: string,
  version: number,
  fileName: string,
  projectName: string
): Promise<void> => {
  const response = await fetch(`${getApiUrl()}/get-render?projectId=${projectId}&version=${version}`);
  if (!response.ok) {
    throw new Error('Failed to download render');
  }
  const arrayBuffer = await response.arrayBuffer();
  const blob = new Blob([arrayBuffer], { type: 'video/mp4' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const extension: string = fileName.includes('.') ? fileName.split('.').pop() || 'mp4' : 'mp4';
  const sanitizedProjectName: string = projectName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
  const safeProjectNameSegment: string = sanitizedProjectName.length > 0 ? sanitizedProjectName : 'project';
  const isoDate: string = new Date().toISOString();
  const dateSegment: string = isoDate.split('T')[0];
  const downloadFileName: string = `${safeProjectNameSegment}_${version}_${dateSegment}.${extension}`;
  link.download = downloadFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Active API function - switch between mock and real
// const getRender = mockGetRender;
const getRender = realGetRender;

// ============================================================================

// Mock API function for delete_render
// const mockDeleteRender = async (projectId: string, version: number): Promise<{ success: boolean }> => {
// Simulate API delay
// await new Promise(resolve => setTimeout(resolve, 300));
  
// return { success: true };
// };

 const realDeleteRender = async (projectId: string, version: number): Promise<{ success: boolean }> => {
  const response = await fetch(`${getApiUrl()}/delete-render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, version })
  });
  if (!response.ok) throw new Error('Failed to delete render');
  return response.json();
};

// Active API function - switch between mock and real
const deleteRender = realDeleteRender;

// ============================================================================

const Renders: React.FC = () => {
  const { selectedProject } = useProject();
  const navigate = useNavigate();
  const [renders, setRenders] = useState<RenderItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRenders = useCallback(async () => {
    if (!selectedProject) {
      setError('No project selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await listRenders(selectedProject.id);
      setRenders(data);
    } catch (err) {
      console.error('Error fetching renders:', err);
      setError('Failed to load renders');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    fetchRenders();
    
    // Auto-refresh every 10 seconds to show progress updates
    const interval = setInterval(fetchRenders, 10000);
    
    return () => clearInterval(interval);
  }, [selectedProject, fetchRenders]);

  const handleDownload = async (version: number, fileName: string) => {
    if (!selectedProject) return;
    
    try {
      await getRender(selectedProject.id, version, fileName, selectedProject.name);
    } catch (err) {
      console.error('Error downloading render:', err);
      alert('Failed to download render');
    }
  };

  const handleDelete = async (version: number) => {
    if (!selectedProject) return;
    
    if (!window.confirm(`Are you sure you want to delete render version ${version}?`)) {
      return;
    }
    
    try {
      await deleteRender(selectedProject.id, version);
      // Refresh the list after deletion
      await fetchRenders();
    } catch (err) {
      console.error('Error deleting render:', err);
      alert('Failed to delete render');
    }
  };

  const formatDateTime = (dateString: string): string => {
    // Parse the UTC date string and convert to user's local timezone
    const utcDate = new Date(dateString + 'Z'); // Add 'Z' to explicitly mark as UTC
    const localDate = toZonedTime(utcDate, Intl.DateTimeFormat().resolvedOptions().timeZone);
    return format(localDate, 'dd/MM/yyyy - HH:mm:ss');
  };

  const StatusCellRenderer = (props: any) => {
    const status: string = props.value;
    const statusNum: number = parseInt(status, 10);
    
    if (statusNum === -1) {
      return <span style={{ color: 'red', fontWeight: 'bold' }}>Failed</span>;
    } else if (statusNum === 100) {
      return <span style={{ color: 'green', fontWeight: 'bold' }}>Complete</span>;
    } else if (statusNum === 0) {
      return <span style={{ color: 'orange', fontWeight: 'bold' }}>Pending</span>;
    } else {
      return <span style={{ color: 'orange', fontWeight: 'bold' }}>{statusNum}%</span>;
    }
  };

  const ActionsCellRenderer = (props: any) => {
    const version: number = props.data.version;
    const fileName: string = props.data.file_name;
    const status: string = props.data.status;
    const statusNum: number = parseInt(status, 10);
    const isComplete: boolean = statusNum === 100;
    
    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={() => handleDownload(version, fileName)}
          disabled={!isComplete}
          style={{
            padding: '4px 12px',
            cursor: isComplete ? 'pointer' : 'not-allowed',
            opacity: isComplete ? 1 : 0.5,
            backgroundColor: isComplete ? '#4CAF50' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          Download
        </button>
        <button
          onClick={() => handleDelete(version)}
          style={{
            padding: '4px 12px',
            cursor: 'pointer',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
          }}
        >
          Delete
        </button>
      </div>
    );
  };

  const columnDefs: ColDef[] = [
    { field: 'version', headerName: 'Version', sortable: true, filter: true, width: 100 },
    { 
      field: 'status', 
      headerName: 'Status', 
      sortable: true, 
      filter: true, 
      width: 120,
      cellRenderer: StatusCellRenderer
    },
    { 
      field: 'created_at', 
      headerName: 'Created At', 
      sortable: true, 
      filter: true, 
      width: 200,
      valueFormatter: (params: any) => params.value ? formatDateTime(params.value) : ''
    },
    { field: 'file_name', headerName: 'File Name', sortable: true, filter: true, width: 150 },
    { 
      field: 'video_settings.background_color', 
      headerName: 'BG Color', 
      sortable: true, 
      filter: true, 
      width: 160,
      valueGetter: (params: any) => params.data?.video_settings?.background_color
    },
    { 
      field: 'video_settings.fps_num', 
      headerName: 'FPS', 
      sortable: true, 
      filter: true, 
      width: 90,
      valueGetter: (params: any) => params.data?.video_settings?.fps_num
    },
    { 
      field: 'video_settings.video_width', 
      headerName: 'Width', 
      sortable: true, 
      filter: true, 
      width: 160,
      valueGetter: (params: any) => params.data?.video_settings?.video_width
    },
    { 
      field: 'video_settings.transparent', 
      headerName: 'Transparent', 
      sortable: true, 
      filter: true, 
      width: 160,
      valueGetter: (params: any) => params.data?.video_settings?.transparent
    },
    { 
      field: 'video_settings.video_time', 
      headerName: 'Duration (s)', 
      sortable: true, 
      filter: true, 
      width: 160,
      valueGetter: (params: any) => params.data?.video_settings?.video_time
    },
    { 
      field: 'video_settings.aspect_ratio', 
      headerName: 'Aspect Ratio', 
      sortable: true, 
      filter: true, 
      width: 140,
      valueGetter: (params: any) => params.data?.video_settings?.aspect_ratio
    },
    {
      field: 'video_settings.video_format',
      headerName: 'Video Format',
      sortable: true,
      filter: true,
      width: 180,
      valueGetter: (params: any) => params.data?.video_settings?.video_format
    },
    {
      field: 'actions',
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      filter: false,
      width: 200,
      pinned: 'right'
    }
  ];

  // Mandatory: selectedProject must exist to render this component
  if (!selectedProject) {
    return null;
  }

  return (
    <div className="renders-page">
      <button 
        onClick={() => navigate('/app')}
        className="fancy-button"
        style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}
      >
        <ArrowBackIcon style={{ fontSize: '18px' }} />
        Back to Editor
      </button>
      <h1>Renders - {selectedProject.name}</h1>
      
      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
          {error}
        </div>
      )}
      
      {loading && renders.length === 0 ? (
        <p>Loading renders...</p>
      ) : (
        <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
          <AgGridReact
            rowData={renders}
            columnDefs={columnDefs}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true
            }}
            animateRows={true}
            rowSelection="single"
          />
        </div>
      )}
    </div>
  );
};

export default Renders;

